import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class AesCryptoService {
  private readonly algorithm = "AES-CBC";
  private readonly iv = new Uint8Array(16);
  private keyPromise: Promise<CryptoKey> | null = null;

  public isEnabled(): boolean {
    return Boolean(environment.encryption?.enabled);
  }

  public async encrypt(value: unknown): Promise<string> {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const plainText =
      typeof value === "string" ? value : JSON.stringify(value);
    const encrypted = await crypto.subtle.encrypt(
      { name: this.algorithm, iv: this.toArrayBuffer(this.iv) },
      await this.getKey(),
      this.toArrayBuffer(new TextEncoder().encode(plainText)),
    );

    return this.arrayBufferToBase64(encrypted);
  }

  public async decrypt(value: string): Promise<string> {
    if (!value) {
      return null;
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: this.algorithm, iv: this.toArrayBuffer(this.iv) },
      await this.getKey(),
      this.base64ToArrayBuffer(value.trim()),
    );

    return new TextDecoder().decode(decrypted);
  }

  public parseDecryptedJson(value: string): unknown {
    if (!value) {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private async getKey(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      const keyMaterial = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(environment.encryption.secretKey),
        "PBKDF2",
        false,
        ["deriveKey"],
      );

      this.keyPromise = crypto.subtle.deriveKey(
        {
          name: "PBKDF2",
          salt: new TextEncoder().encode(environment.encryption.salt),
          iterations: 65536,
          hash: "SHA-256",
        },
        keyMaterial,
        { name: this.algorithm, length: 256 },
        false,
        ["encrypt", "decrypt"],
      );
    }

    return this.keyPromise;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary);
  }

  private base64ToArrayBuffer(value: string): ArrayBuffer {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return this.toArrayBuffer(bytes);
  }

  private toArrayBuffer(value: Uint8Array): ArrayBuffer {
    return value.buffer.slice(
      value.byteOffset,
      value.byteOffset + value.byteLength,
    ) as ArrayBuffer;
  }
}
